'use client';

import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { X, ArrowLeft, Loader2 } from 'lucide-react';
import { useI18n } from '@/hooks/useI18n';

type VerificationModalProps = {
  onClose: () => void;
  onVerificationComplete: (verificationNonce: string) => void;
};

export const VerificationModal: React.FC<VerificationModalProps> = ({
  onClose,
  onVerificationComplete
}) => {
  const { t } = useI18n();
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  // Cooldown timer for resend
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleSendCode = async () => {
    if (!email || !email.includes('@')) {
      setError(t('verification.errors.invalidEmail'));
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      // Stub API call - sends verification code
      const response = await fetch('/api/verification/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (response.ok) {
        // Log the generated code for testing
        console.log('Verification code sent to email:', email);
        console.log('Generated code (for testing):', data.code);

        setStep('code');
        setResendCooldown(60);
      } else {
        setError(data.error || t('verification.errors.sendFailed'));
      }
    } catch (err) {
      setError(t('verification.errors.networkError'));
      console.error('Send code error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0) return;

    setCode('');
    setError('');
    await handleSendCode();
  };

  const handleVerifyCode = async () => {
    if (!code || code.length !== 6) {
      setError(t('verification.errors.invalidCode'));
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      // Stub API call - verifies code and returns receipt ID
      const response = await fetch('/api/verification/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code })
      });

      const data = await response.json();

      if (response.ok) {
        console.log('Verification successful! Verification nonce:', data.verificationNonce);
        onVerificationComplete(data.verificationNonce);
        onClose();
      } else {
        setError(data.error || t('verification.errors.invalidCode'));
      }
    } catch (err) {
      setError(t('verification.errors.networkError'));
      console.error('Verify code error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (step === 'email') {
        handleSendCode();
      } else {
        handleVerifyCode();
      }
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        {step === 'code' ? (
          <button
            onClick={() => setStep('email')}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ArrowLeft className="w-6 h-6 text-gray-700" />
          </button>
        ) : (
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-6 h-6 text-gray-700" />
          </button>
        )}
        <h1 className="text-xl font-semibold text-gray-900">{t('app.subtitle')}</h1>
        <div className="w-10" /> {/* Spacer for centering */}
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-8">
        {step === 'email' ? (
          <>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('verification.title')}</h2>
            <p className="text-gray-600 text-center mb-8 max-w-md">
              {t('verification.subtitle')}
            </p>

            <div className="w-full max-w-md space-y-4">
              <div>
                <label className="text-gray-900 font-medium mb-2 block">{t('verification.emailLabel')}</label>
                <Input
                  type="email"
                  placeholder={t('verification.emailPlaceholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 h-14 text-lg"
                  disabled={isSubmitting}
                />
              </div>

              {error && (
                <p className="text-red-600 text-sm">{error}</p>
              )}

              <Button
                onClick={handleSendCode}
                disabled={isSubmitting || !email}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white h-14 text-lg font-semibold"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    {t('verification.sendingButton')}
                  </>
                ) : (
                  t('verification.sendCodeButton')
                )}
              </Button>
            </div>
          </>
        ) : (
          <>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('verification.codeTitle')}</h2>
            <p className="text-gray-600 text-center mb-8 max-w-md">
              {t('verification.codeSubtitle', { email })}
            </p>

            <div className="w-full max-w-md space-y-4">
              <div>
                <label className="text-gray-900 font-medium mb-2 block">{t('verification.codeLabel')}</label>
                <Input
                  type="text"
                  placeholder={t('verification.codePlaceholder')}
                  value={code}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setCode(value);
                    setError('');
                  }}
                  onKeyPress={handleKeyPress}
                  className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 h-14 text-lg text-center tracking-widest"
                  maxLength={6}
                  disabled={isSubmitting}
                />
              </div>

              {error && (
                <p className="text-red-600 text-sm">{error}</p>
              )}

              <Button
                onClick={handleVerifyCode}
                disabled={isSubmitting || code.length !== 6}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white h-14 text-lg font-semibold"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    {t('verification.verifyingButton')}
                  </>
                ) : (
                  t('verification.verifyButton')
                )}
              </Button>

              <div className="text-center">
                <button
                  onClick={handleResendCode}
                  disabled={resendCooldown > 0}
                  className="text-gray-600 hover:text-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('verification.resendText')}{' '}
                  <span className="text-blue-600">
                    {resendCooldown > 0 ? t('verification.resendCountdown', { seconds: resendCooldown }) : t('verification.resendButton')}
                  </span>
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
